// One place that answers "where does this thing actually live?" — used by the
// notification bell and the activity/daily-report feeds so a tap lands on the item
// itself (that PO card, that drilling card, that submittal) instead of dumping the
// user on the job page and making them hunt.
//
// Everything degrades gracefully: if there's no better target we fall back to the
// job, and if there's no job either we return null and the row simply isn't clickable.

const WORKFLOW_TYPES = ['vertical_drilling', 'horizontal_drilling', 'loops', 'nve', 'usa_digs', 'small_jobs', 'fiber', 'permits', 'shop', 'submittals'];

const job = id => (id ? `/admin/new-projects/${id}` : null);
// Who is looking: the session's role ('admin' for the owner login).
const viewerRole = () => { try { const s = JSON.parse(localStorage.getItem('titan_session') || 'null'); return s?.role || s?.user?.role || ''; } catch { return ''; } };

// A workflow card opens on its board via ?card=<subJobId> (see AdminWorkflows).
const workflowCard = (type, subJobId) =>
  (type && subJobId ? `/admin/workflows/${type}?card=${subJobId}` : type ? `/admin/workflows/${type}` : null);

// data = the notification's parsed `data`, or an activity row's `meta`.
// Returns a route string, or null when there's nothing sensible to open.
export function linkForItem({ type, scope, projectId, data } = {}) {
  const d = data || {};
  const pid = projectId || d.projectId || d.project_id || null;

  // A pointer was aimed by a person at a page they were looking at. Nothing to
  // resolve — that IS the answer, and it beats anything inferred.
  if (typeof d.path === 'string' && d.path.startsWith('/')) return d.path;

  // Most specific first — an id for the exact record beats the job every time.
  if (d.woId) return `/admin/wo-billing?wo=${d.woId}`;
  // A PO: the office page for the office, the phone Purchase Orders page for the field and shop
  // (the admin page bounced them home — Oct 18 2026).
  if (d.poId) { const r = viewerRole(); return ['admin', 'Admin', 'Executive', 'ProjectManager', 'Accounting', 'OfficeStaff'].includes(r) ? `/admin/purchase-orders?open=${d.poId}` : `/shop/pos?po=${d.poId}`; }
  if (d.orderId) return `/admin/shop?tab=shop_order&order=${d.orderId}`;
  // A priority (directive): the person it was SENT to lands on their own priorities —
  // a PM used to be dropped on the Executive hub's send form (Ryan, Sep 30 2026).
  if (d.directiveId) { const r = viewerRole(); return r === 'admin' || r === 'Executive' ? `/admin/directives?open=${d.directiveId}` : ['ProjectManager', 'Accounting', 'OfficeStaff'].includes(r) ? `/admin/priorities?open=${d.directiveId}` : '/directives'; }
  // A field Ask-AI answer opens the field assistant; a Chief-of-Staff answer opens COS.
  // /cos only lets the two COS users in, so sending a shop hand there bounced them home.
  if (d.conversationId) return d.convKind === 'ask_ai' ? '/ask' : '/cos';
  if (d.request_id) return '/admin/timesheets';
  // Accidents and pre-trip defects live under Shop → Driver Tools, each on its own
  // sub-tab; open the exact record, not the section.
  if (d.eventId) return `/admin/shop?tab=driver&sub=accident&event=${d.eventId}`;
  if (d.reportId) return `/admin/shop?tab=driver&sub=inspections&report=${d.reportId}`;
  // An unmatched material receipt: open it on the Material tab so it can be matched.
  if (d.receiptId && !pid) return `/admin/shop?tab=material&receipt=${d.receiptId}`;
  if (d.submittalItemId && pid) return `/admin/jobs/${pid}?pillow=submittals`;

  // Posted from a workflow card → open that card.
  const wfType = d.workflowType || (WORKFLOW_TYPES.includes(scope) ? scope : null);
  const subJobId = d.subJobId || d.sub_job_id || null;
  if (wfType && subJobId) return workflowCard(wfType, subJobId);
  if (wfType && !pid) return workflowCard(wfType, null);

  return job(pid);
}

// Short label for what the link opens — shown as the "· open PO ›" style hint.
export function linkLabelForItem({ type, scope, data } = {}) {
  const d = data || {};
  if (d.path) return 'open it';
  if (d.woId) return 'open the invoice';
  if (d.poId) return 'open PO';
  if (d.orderId) return 'open shop order';
  if (d.directiveId) return 'open directive';
  if (d.conversationId) return 'open chat';
  if (d.submittalItemId) return 'open submittal';
  if (d.receiptId) return 'open material';
  if (d.request_id) return 'open request';
  if (d.eventId) return 'open accident';
  if (d.reportId) return 'open inspection';
  if (d.workflowType || WORKFLOW_TYPES.includes(scope)) return 'open card';
  return 'open job';
}
